/**
 * Extract a frame as a File object from a video at a specific timestamp.
 *
 * @param videoFile - The video file to extract the frame from.
 * @param timeInSeconds - The time in seconds to capture the frame.
 * @returns Promise<File> - A Promise that resolves to a File object containing the extracted frame.
 */
const extractFrameAsFile = (
  videoFile: File,
  timeInSeconds: number
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");

    // Read the video file as a URL
    const fileURL = URL.createObjectURL(videoFile);

    video.src = fileURL;
    video.crossOrigin = "anonymous";
    video.muted = true; // Prevent video audio from playing
    video.style.display = "none"; // Hide the video element

    // Load the video and wait until metadata is available
    video.onloadedmetadata = () => {
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Set the current time to the desired frame time
      video.currentTime = timeInSeconds;
    };

    // Event handler for when video has seeked to the specified time
    video.onseeked = () => {
      const context = canvas.getContext("2d");
      if (context) {
        // Draw the video frame onto the canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to a Blob and create a File object
        canvas.toBlob((blob) => {
          if (blob) {
            const frameFile = new File(
              [blob],
              `frame_at_${timeInSeconds}s.png`,
              { type: "image/png" }
            );
            resolve(frameFile);
          } else {
            reject(new Error("Failed to capture frame as a file."));
          }
        }, "image/png");
      }
    };

    // Error handling
    video.onerror = () => reject(new Error("Failed to load video file."));
  });
};

export default extractFrameAsFile;
